/**
 * Trader Profile Tooltip
 * Shows trader profile information when hovering over wallet execution markers on the chart
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { Star, TrendingUp, Copy, Check, ExternalLink } from "lucide-react";
import type { PumpFunProfile } from "@/lib/pumpfunProfile";

interface TraderProfileTooltipProps {
  profile: PumpFunProfile;
  solAmount: number;
  isBuy: boolean;
  position: { x: number; y: number };
}

export function TraderProfileTooltip({
  profile,
  solAmount,
  isBuy,
  position,
}: TraderProfileTooltipProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(profile.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSolscan = () => {
    window.open(`https://solscan.io/account/${profile.address}`, "_blank");
  };

  return createPortal(
    <div
      className="fixed z-[99999] animate-in fade-in zoom-in-95 duration-150"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: "translate(-50%, -100%) translateY(-12px)",
      }}
    >
      <div className="relative bg-[#0b0e11] border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[280px]">
        {/* Arrow */}
        <div
          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-[#0b0e11] border-r border-b border-white/10 rotate-45"
        />

        {/* Header */}
        <div
          className={`p-3 border-b border-white/5 ${
            isBuy ? "bg-emerald-500/10" : "bg-rose-500/10"
          }`}
        >
          <div className="flex items-center gap-3">
            <img
              src={profile.avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${profile.address}`}
              alt={profile.username || "Trader"}
              className="size-10 rounded-full ring-2 ring-white/10 bg-black/50 object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-white truncate">
                  {profile.username || `Trader ${profile.address?.slice(0, 4)}...${profile.address?.slice(-4)}`}
                </h4>
                {profile.isWhale && (
                  <Star className="size-3 text-amber-400 fill-amber-400 shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] font-mono text-white/50 truncate">
                  {profile.address ? `${profile.address.slice(0, 6)}...${profile.address.slice(-4)}` : "Unknown"}
                </span>
                <button
                  onClick={handleCopy}
                  className="text-white/40 hover:text-white transition-colors shrink-0"
                >
                  {copied ? (
                    <Check className="size-3 text-emerald-400" />
                  ) : (
                    <Copy className="size-3" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trade Info */}
        <div className="p-3 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">
                {isBuy ? "Buy Order" : "Sell Order"}
              </div>
              <div
                className={`text-lg font-black ${
                  isBuy ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {solAmount.toFixed(4)} SOL
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-wider text-white/40 mb-0.5">
                Action
              </div>
              <div
                className={`text-xs font-bold ${
                  isBuy ? "text-emerald-400" : "text-rose-400"
                }`}
              >
                {isBuy ? "BUY" : "SELL"}
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Stats */}
        <div className="p-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-black/40 rounded-lg p-2 border border-white/5">
              <div className="text-[9px] text-white/40 uppercase mb-0.5">
                SOL Balance
              </div>
              <div className="text-xs font-bold text-white">
                {(profile.solBalance || 0).toFixed(2)} SOL
              </div>
            </div>
            <div className="bg-black/40 rounded-lg p-2 border border-white/5">
              <div className="text-[9px] text-white/40 uppercase mb-0.5">
                Tokens
              </div>
              <div className="text-xs font-bold text-white">
                {profile.tokenCount || 0}
              </div>
            </div>
            {profile.winRate && (
              <div className="bg-black/40 rounded-lg p-2 border border-white/5">
                <div className="text-[9px] text-white/40 uppercase mb-0.5">
                  Win Rate
                </div>
                <div className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                  <TrendingUp className="size-3" /> {profile.winRate.toFixed(1)}%
                </div>
              </div>
            )}
            {profile.totalTrades && (
              <div className="bg-black/40 rounded-lg p-2 border border-white/5">
                <div className="text-[9px] text-white/40 uppercase mb-0.5">
                  Total Trades
                </div>
                <div className="text-xs font-bold text-white">
                  {profile.totalTrades}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-2 border-t border-white/5">
          <button
            onClick={handleSolscan}
            className="w-full flex items-center justify-center gap-2 p-2 rounded-lg text-[10px] font-bold text-white/60 hover:text-white hover:bg-white/5 transition-all"
          >
            <ExternalLink className="size-3" /> View on Solscan
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
